import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Trash2, Eye, EyeOff, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { API } from '../App';

/** Referral codes admin CRUD + stats. Anyone can be a referrer: pastor, vendor, influencer, friend. */
export default function ReferralsAdmin() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ code: '', referrer_name: '', organization: '', referrer_email: '', commission_pct: 5 });
  const [submitting, setSubmitting] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/referrals`);
      setCodes(r.data.codes || []);
    } catch { toast.error('Could not load referral codes'); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.referrer_name.trim()) { toast.error('Code and referrer name required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/referrals`, form);
      toast.success(`Code ${form.code.toUpperCase()} created`);
      setForm({ code: '', referrer_name: '', organization: '', referrer_email: '', commission_pct: 5 });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setSubmitting(false); }
  };

  const toggle = async (code, active) => {
    try { await axios.put(`${API}/admin/referrals/${code}/toggle?active=${!active}`); reload(); }
    catch { toast.error('Could not update'); }
  };
  const remove = async (code) => {
    if (!window.confirm(`Delete code ${code}? Past sales remain logged; future uses are blocked.`)) return;
    try { await axios.delete(`${API}/admin/referrals/${code}`); toast.success('Deleted'); reload(); }
    catch { toast.error('Could not delete'); }
  };

  return (
    <div className="space-y-6" data-testid="referrals-admin">
      <div>
        <h2 className="text-2xl font-heading font-medium">Referral Codes</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Give any partner — pastor, influencer, vendor, friend — a personal code. They earn the % below on every order using it.
        </p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Plus className="h-4 w-4" /> Create Code</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input placeholder="Code (e.g. SARAH2026)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} required data-testid="referral-code" />
            <Input placeholder="Referrer name" value={form.referrer_name} onChange={(e) => setForm({ ...form, referrer_name: e.target.value })} required data-testid="referral-referrer" />
            <Input placeholder="Organization or church (optional)" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} data-testid="referral-org" />
            <Input type="email" placeholder="Referrer email (optional, for payouts)" value={form.referrer_email} onChange={(e) => setForm({ ...form, referrer_email: e.target.value })} data-testid="referral-email" />
            <Input type="number" min="0" max="50" step="0.5" placeholder="Commission %" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: Number(e.target.value) })} data-testid="referral-pct" />
            <Button type="submit" disabled={submitting} data-testid="referral-submit">{submitting ? 'Saving…' : 'Create Code'}</Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">All Codes ({codes.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-muted-foreground">Loading…</p>
            : codes.length === 0 ? <p className="text-sm text-muted-foreground">No codes yet.</p>
            : (
              <ul className="divide-y divide-border/40">
                {codes.map((c) => {
                  const refName = c.referrer_name || c.pastor_name || '—';
                  const org = c.organization || c.church_name;
                  return (
                    <li key={c.code} className="py-3 flex items-center gap-3" data-testid={`referral-row-${c.code}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-mono font-semibold text-foreground">{c.code}</span>
                          <Badge variant={c.is_active ? 'default' : 'secondary'} className="text-xs">{c.is_active ? 'Active' : 'Disabled'}</Badge>
                          <span className="text-xs text-muted-foreground">{c.commission_pct}%</span>
                        </div>
                        <p className="text-sm text-foreground/80">{refName}{org && <span className="text-muted-foreground"> — {org}</span>}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {c.total_uses || 0} use{(c.total_uses || 0) === 1 ? '' : 's'} • ${(c.total_commission_earned || 0).toFixed(2)} earned • ${(c.total_paid_out || 0).toFixed(2)} paid out
                        </p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => toggle(c.code, c.is_active)} data-testid={`referral-toggle-${c.code}`}>
                        {c.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => remove(c.code)} data-testid={`referral-delete-${c.code}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
        </CardContent>
      </Card>
    </div>
  );
}
